import { useEffect, useState } from 'react'
import api from '../services/api'
import TeamHelmet from '../components/TeamHelmet'
import UserAvatar from '../components/UserAvatar'
import './MatchupCallTracker.css'

interface MatchupCallUser {
  id: number
  username: string
  displayName: string
  discordId: string | null
  discordAvatar: string | null
  teamsCalled: string[]
}

/**
 * Shows each competitor's recorded coin-toss team calls.
 */
export default function MatchupCallTracker() {
  const [users, setUsers] = useState<MatchupCallUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadTracker = async () => {
      try {
        setLoading(true)
        const response = await api.get<MatchupCallUser[]>('/users/matchup-calls')
        setUsers(response.data ?? [])
        setError(null)
      } catch (err: any) {
        console.error('Failed to load matchup call tracker:', err)
        setError(err.response?.data?.error || 'Failed to load matchup call tracker.')
      } finally {
        setLoading(false)
      }
    }

    loadTracker()
  }, [])

  if (loading) {
    return <div className="matchup-call-tracker loading">Loading matchup call tracker...</div>
  }

  if (error) {
    return (
      <div className="matchup-call-tracker">
        <div className="error-message">{error}</div>
      </div>
    )
  }

  return (
    <div className="matchup-call-tracker">
      <h1>Matchup Call Tracker</h1>
      <p className="matchup-call-subtitle">
        Teams each competitor called after winning the coin toss.
      </p>

      <div className="matchup-call-grid">
        {users.map((user) => (
          <section key={user.id} className="matchup-call-card">
            <div className="matchup-call-header">
              <UserAvatar
                displayName={user.displayName}
                discordId={user.discordId}
                discordAvatar={user.discordAvatar}
                size="md"
              />
              <div className="matchup-call-user">
                <h2>{user.displayName}</h2>
                <p>
                  {user.teamsCalled.length} call{user.teamsCalled.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>

            {user.teamsCalled.length > 0 ? (
              <div className="matchup-call-teams">
                {user.teamsCalled.map((team, index) => (
                  <div key={`${user.id}-${team}-${index}`} className="matchup-call-chip">
                    <TeamHelmet abbreviation={team} size="sm" className="matchup-call-helmet" />
                    <span>{team}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="matchup-call-empty">No recorded coin toss calls yet.</p>
            )}
          </section>
        ))}
      </div>
    </div>
  )
}
